// The clerk's head. Pure logic: no DOM, no three.js, no network - so it can be tested in
// plain Node against the real catalogue.
//
// WHAT IT IS. A retrieval clerk with a record-shop manner. You type what you want in your
// own words; it works out what you mean (a vibe, an artist you like, a decade, a section, a
// question about how the shop works) and answers from what is actually on the shelves,
// with the specific records, why each fits, and where to find them. It never invents a
// record: every pick is a real catalogue entry.
//
// WHAT IT IS NOT. A language model. It reads the words you give it against the catalogue's
// own vocabulary (genres, subgenres, decades, the 414 real artists each record is "for fans
// of", the mood and imagery written for each cover). It is good at "something like Alice
// Coltrane", "70s funk", "late night, moody", "what's new". It will not follow a long,
// open-ended conversation. clerkUi.js can hand a question to a real model when
// VITE_CLERK_URL is set, and falls back to this when that is slow or down.
//
// Records the clerk can search: { id, title, artist, year, era, label, genreId, subgenreId,
// subgenreLabel, tracks, similarArtist, sonautoPrompt, sonautoTags } - the catalogue's shape.

const PICKS = 3;

// -------------------------------------------------------------------------- text
const STOP = new Set(('a an the and or but of in on at to for from with by is are was be been am do does did i me my you your '
  + 'we it its this that these those some any got have has had can could would should will just really very so than then '
  + 'please thanks want wanna looking look need find get give show tell something anything stuff music record records '
  + 'album albums lp vinyl one ones kind sort bit little').split(' '));

function stem(word) {
  if (word.length > 4 && word.endsWith('ies')) return word.slice(0, -3) + 'y';
  if (word.length > 3 && word.endsWith('s') && !word.endsWith('ss') && !word.endsWith('us')) return word.slice(0, -1);
  return word;
}

const normalise = (text) => String(text || '').toLowerCase()
  .replace(/&/g, ' and ').replace(/['’`]/g, '').replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();

const tokens = (text) => normalise(text).split(' ').filter(Boolean).map(stem);
const meaningful = (list) => list.filter((t) => t.length > 1 && !STOP.has(t));

// Small deterministic hash, so a given question gets a given turn of phrase.
function hash(text) {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) { h ^= text.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}
const choose = (list, seed) => list[seed % list.length];

// ----------------------------------------------------------------------- vocabulary
// Broad words people use, mapped to a genre. Subgenre names (bebop, delta, boom bap...) need
// no table: they are in the catalogue and are matched straight from each record's own text.
const GENRE_WORDS = {
  jazz: ['jazz', 'bebop', 'swing', 'sax', 'saxophone', 'trumpet', 'coltrane'],
  soul: ['soul', 'funk', 'funky', 'motown', 'northern soul', 'groove'],
  hiphop: ['hip hop', 'hiphop', 'rap', 'boom bap', 'mc', 'breakbeat', 'breaks'],
  reggae: ['reggae', 'dub', 'dancehall', 'ska', 'rocksteady', 'roots'],
  blues: ['blues', 'bluesy', 'delta'],
  latin: ['latin', 'salsa', 'boogaloo', 'bossa', 'cumbia', 'mambo', 'afro cuban'],
  rnb: ['r and b', 'rnb', 'rhythm and blues', 'slow jam', 'quiet storm', 'new jack'],
  electronic: ['electronic', 'synth', 'synthesizer', 'techno', 'house', 'electro', 'ambient', 'kosmische'],
  disco: ['disco', 'boogie', 'philly', 'nu disco'],
  rockpsych: ['rock', 'psych', 'psychedelic', 'punk', 'garage', 'prog', 'glam', 'krautrock', 'guitar'],
  gospel: ['gospel', 'church', 'choir', 'spiritual'],
  soundtracks: ['soundtrack', 'soundtracks', 'library', 'film', 'score', 'tv theme', 'cinematic', 'movie'],
  world: ['world', 'afrobeat', 'african', 'international', 'highlife', 'ethiopian', 'brazilian'],
  classical: ['classical', 'orchestra', 'orchestral', 'baroque', 'symphony', 'piano', 'chamber', 'string quartet'],
  folk: ['folk', 'acoustic', 'country', 'singer songwriter', 'bluegrass', 'americana'],
  pop: ['pop', 'synthpop', 'city pop', 'bubblegum', 'sunshine pop'],
};

// A feeling is not a field in the catalogue, so each one is translated into words that ARE
// in it: the subgenre names, the mood text and the cover imagery.
const VIBES = [
  { name: 'weird', blurb: 'properly strange',    ask: ['weird', 'weirder', 'strange', 'odd', 'experimental', 'avant', 'out there', 'trippy', 'freaky', 'unusual', 'obscure'],
    want: ['psych', 'krautrock', 'experimental', 'avant', 'cosmic', 'prog', 'dubwise', 'mysticism', 'abstract', 'futurism', 'spiritual', 'library', 'airbrushed'] },
  { name: 'chill', blurb: 'easy and mellow',    ask: ['chill', 'mellow', 'relax', 'relaxing', 'calm', 'smooth', 'laid back', 'easy', 'soft', 'gentle', 'unwind', 'lazy'],
    want: ['smooth', 'lovers', 'ambient', 'easy', 'bossa', 'soft', 'mellow', 'intimacy', 'quiet', 'sunshine', 'porch', 'lazy', 'cool'] },
  { name: 'dark', blurb: 'smoky, late-night mood',     ask: ['dark', 'moody', 'noir', 'late night', 'night', 'midnight', 'smoky', 'brooding', 'gloomy', 'after hours'],
    want: ['smoky', 'night', 'basement', 'noir', 'dark', 'minor', 'moody', 'dub', 'freeway', 'blues', 'haze'] },
  { name: 'upbeat', blurb: 'made to move to',   ask: ['upbeat', 'dance', 'party', 'happy', 'fun', 'energetic', 'energy', 'danceable', 'groove', 'bouncy', 'summer', 'sunny', 'feel good'],
    want: ['disco', 'boogie', 'funk', 'dance', 'salsa', 'sunshine', 'party', 'block', 'heat', 'club', 'bandstand'] },
  { name: 'sad', blurb: 'quiet and a little blue',      ask: ['sad', 'melancholy', 'melancholic', 'heartbreak', 'lonely', 'blue', 'crying', 'bittersweet', 'sorrow', 'rainy', 'rain', 'cozy', 'winter'],
    want: ['blues', 'ballad', 'lonesome', 'folk', 'intimacy', 'torch', 'dusk', 'porch', 'quiet', 'piano', 'slow'] },
  { name: 'romantic', blurb: 'slow and romantic', ask: ['romantic', 'romance', 'love', 'sexy', 'sensual', 'date', 'seduction', 'slow dance', 'intimate'],
    want: ['romantic', 'intimacy', 'lovers', 'ballad', 'slow', 'quiet', 'crossover', 'cinematic', 'soul'] },
  { name: 'heavy', blurb: 'raw and loud',    ask: ['heavy', 'loud', 'hard', 'angry', 'aggressive', 'raw', 'gritty', 'fast', 'intense', 'wild'],
    want: ['punk', 'garage', 'hard', 'metal', 'grit', 'crew', 'hardcore', 'rock', 'chrome', 'smoke'] },
  { name: 'spiritual', blurb: 'uplifting', ask: ['spiritual', 'sacred', 'uplifting', 'transcendent', 'meditative', 'holy', 'cosmic'],
    want: ['spiritual', 'gospel', 'cosmic', 'modal', 'mysticism', 'choir', 'orchestral', 'gravitas'] },
];

const WORD_NUMBERS = { fifties: 1950, sixties: 1960, seventies: 1970, eighties: 1980, nineties: 1990 };

// ---------------------------------------------------------------------- the index
function mood(prompt) {
  const tail = (String(prompt || '').match(/Mood: [a-z0-9 -]*?-leaning (.*?)\. Imagery/) || [])[1] || '';
  const imagery = (String(prompt || '').match(/Imagery cues from the album cover: (.*?), worn collectible/) || [])[1] || '';
  return { tail, imagery };
}

function buildIndex(records, genreLabel) {
  const docs = records.map((r) => {
    const { tail, imagery } = mood(r.sonautoPrompt);
    const weight = new Map();
    const add = (text, w) => { for (const t of meaningful(tokens(text))) weight.set(t, (weight.get(t) || 0) + w); };
    add(r.subgenreLabel, 4);
    add(genreLabel(r.genreId), 3);
    add((r.sonautoTags || []).join(' '), 3);
    add(r.similarArtist, 5);
    add(r.artist, 4);
    add(r.title, 2.5);
    add(r.label, 1);
    add(tail, 2);
    add(imagery, 1.5);
    weight.set('d' + Math.floor(Number(r.year) / 10) * 10, 3);
    return { r, weight, norm: { artist: normalise(r.artist), sim: normalise(r.similarArtist), sub: normalise(r.subgenreLabel), title: normalise(r.title) },
      text: normalise([tail, imagery].join(' ')) };
  });
  const df = new Map();
  for (const d of docs) for (const t of d.weight.keys()) df.set(t, (df.get(t) || 0) + 1);
  const idf = (t) => Math.log(1 + docs.length / (1 + (df.get(t) || 0)));
  return { docs, idf, df };
}

// ------------------------------------------------------------------------- brain
function createClerkBrain({ records, genres = [], locate = () => null, price = 125, now = () => new Date() }) {
  const genreById = Object.fromEntries(genres.map((g) => [g.id, g]));
  const genreLabel = (id) => (genreById[id] && genreById[id].label) || id;
  const index = buildIndex(records, genreLabel);
  const state = { seen: new Set(), last: null, turns: 0 };

  // artists someone might name: the real ones records are "for fans of", and the shop's own
  const artistNames = new Map();
  for (const d of index.docs) {
    if (d.norm.sim) artistNames.set(d.norm.sim, d.r.similarArtist);
    if (d.norm.artist) artistNames.set(d.norm.artist, d.r.artist);
  }

  // The artist a sentence names, if any: the longest known name found inside it.
  function findArtist(query) {
    let best = null;
    for (const [norm, display] of artistNames) {
      if (norm.length < 4) continue;
      if ((' ' + query + ' ').includes(' ' + norm + ' ') && (!best || norm.length > best.norm.length)) best = { norm, display };
    }
    return best;
  }

  const genresIn = (query) => {
    const found = [];
    for (const [id, words] of Object.entries(GENRE_WORDS)) {
      if (words.some((w) => (' ' + query + ' ').includes(' ' + normalise(w) + ' '))) found.push(id);
    }
    return found;
  };

  const vibesIn = (query) => VIBES.filter((v) => v.ask.some((w) => (' ' + query + ' ').includes(' ' + normalise(w) + ' ')));

  function timeIn(query) {
    const years = [...query.matchAll(/\b(19[5-9]\d|20[0-2]\d)\b/g)].map((m) => Number(m[1]));
    let decade = null;
    const dm = query.match(/\b(?:19)?([5-9]0)s?\b/);
    if (dm && !years.length) decade = 1900 + Number(dm[1]);
    for (const [word, y] of Object.entries(WORD_NUMBERS)) if (query.includes(word)) decade = y;
    return { year: years[0] || null, decade };
  }

  // Rank the whole catalogue against one query. Returns [{ d, score, why: [] }], best first.
  function rank(query, { artist = null, genresWanted = [], vibes = [], when = {}, avoid = new Set() } = {}) {
    const q = [...new Set(meaningful(tokens(query)))];
    const out = [];
    // What kind of music the named artist's own records are, so that an artist with only a
    // couple of records is topped up with their neighbours rather than with whatever else
    // happens to share a word.
    const kin = { genres: new Set(), bins: new Set() };
    if (artist) for (const d of index.docs) {
      if (d.norm.sim === artist.norm || d.norm.artist === artist.norm) { kin.genres.add(d.r.genreId); kin.bins.add(d.r.genreId + '/' + d.r.subgenreId); }
    }
    for (const d of index.docs) {
      let score = 0;
      const why = [];
      for (const t of q) {
        const w = d.weight.get(t);
        if (w) score += index.idf(t) * Math.min(w, 8);
      }
      let named = 0;
      if (artist && (d.norm.sim === artist.norm || d.norm.artist === artist.norm)) {
        named = 40;
        why.push(d.norm.sim === artist.norm ? `for fans of ${d.r.similarArtist}` : `by ${d.r.artist}`);
      } else if (artist && kin.bins.has(d.r.genreId + '/' + d.r.subgenreId)) { score += 10; why.push(`same sort of thing`); }
      else if (artist && kin.genres.has(d.r.genreId)) score += 5;
      // Reasons are only what the line does not already say: the bin and the year are on it.
      if (genresWanted.includes(d.r.genreId)) score += 9;
      if (d.norm.sub && (' ' + query + ' ').includes(' ' + d.norm.sub + ' ')) score += 12;
      for (const v of vibes) {
        const hit = v.want.filter((w) => d.weight.has(stem(w)) || d.text.includes(w));
        if (hit.length) { score += 3.2 * Math.min(hit.length, 3); why.push(v.blurb); }
      }
      if (when.year) {
        const gap = Math.abs(Number(d.r.year) - when.year);
        if (gap === 0) score += 11; else if (gap === 1) score += 4;
      } else if (when.decade && Math.floor(Number(d.r.year) / 10) * 10 === when.decade) score += 7;
      if (avoid.has(d.r.id)) score *= 0.35;
      score += named;          // after the penalty: an exact match always beats an unrelated record
      if (score > 0) out.push({ d, score: score + (hash(query + d.r.id) % 100) / 1000, why });
    }
    return out.sort((a, b) => b.score - a.score);
  }

  // Three picks that are not all the same bin: the shop has ten of each, and ten near-identical
  // answers is not a recommendation.
  function diversify(ranked, count = PICKS) {
    const out = [], perBin = new Map();
    for (const item of ranked) {
      const bin = item.d.r.genreId + '/' + item.d.r.subgenreId;
      if ((perBin.get(bin) || 0) >= 1 && out.length < count) {
        if (ranked.length - out.length > count * 4) continue;
      }
      perBin.set(bin, (perBin.get(bin) || 0) + 1);
      out.push(item);
      if (out.length >= count) break;
    }
    return out;
  }

  function toPick(item) {
    const r = item.d.r;
    const place = locate(r) || null;
    return { id: r.id, title: r.title, artist: r.artist, year: r.year, genreId: r.genreId, subgenreId: r.subgenreId,
      bin: r.subgenreLabel, genre: genreLabel(r.genreId),
      why: [...new Set(item.why)].filter((w) => w !== r.subgenreLabel && w !== genreLabel(r.genreId)).slice(0, 2).join(', '), place };
  }

  const week = () => { const d = now(); const start = new Date(d.getFullYear(), 0, 1); return d.getFullYear() * 100 + Math.floor((d - start) / 6048e5); };

  const SUGGESTIONS = () => {
    const sims = [...new Set(index.docs.map((d) => d.r.similarArtist))];
    const s = hash('suggest' + week() + state.turns);
    return [`Something like ${sims[s % sims.length]}`, 'What came in this week?', 'Late night, moody', '70s funk', 'Surprise me'];
  };

  const line = (p) => `“${p.title}” — ${p.artist} (${p.year}), ${p.bin}`;

  function present(intro, items, { quiet = false } = {}) {
    const picks = items.map(toPick);
    items.forEach((i) => state.seen.add(i.d.r.id));
    const first = picks[0];
    const where = first && first.place && first.place.text ? `\nThe first is ${first.place.text}.` : '';
    // `quiet`: the heading already says why (a named artist, "new this week"), so a reason
    // that only repeats it is dropped from the line, though it stays in the pick's data.
    // Otherwise a reason every pick shares is said once, in the heading, not three times.
    const shared = !quiet && picks.length > 1 && picks[0].why && picks.every((p) => p.why === picks[0].why) ? picks[0].why : '';
    const lead = shared ? `${intro.replace(/:\s*$/, '')} \u2014 ${shared}:` : intro;
    const bullets = picks.map((p) => `\u2022 ${line(p)}${p.why && !shared && !quiet ? ` \u2014 ${p.why}` : ''}`).join('\n');
    return { picks, reply: `${lead}\n${bullets}${where}` };
  }

  const PRICE_LINE = `Every record is ${price} credits. Pull the ones you like, tuck them under your arm with C, then pay at the counter and I'll have a new song cut from each sleeve into your studio library. You can play a 30-second taste of any record in here with F.`;

  function sections() {
    const lines = genres.map((g) => `${g.label} (${index.docs.filter((d) => d.r.genreId === g.id).length})`);
    return `We carry ${index.docs.length} records across ${genres.length} sections, ten to a bin: ${lines.join(', ')}.`;
  }

  // ---- the front door ----------------------------------------------------------
  function ask(text) {
    state.turns++;
    const raw = String(text || '').slice(0, 400);
    const query = normalise(raw);
    const seed = hash(query) + state.turns;
    const result = (intent, reply, picks = []) => ({ intent, reply, picks, suggestions: SUGGESTIONS() });

    if (!query) return result('empty', 'Go on, ask me anything - a mood, an artist you like, a decade. Or just say "surprise me".');

    const greeting = /^(hi|hello|hey|yo|morning|afternoon|evening|sup|howdy|hiya)\b/.test(query) && query.split(' ').length <= 4;
    if (greeting) return result('greeting', choose(['Hello! Looking for anything in particular, or just browsing?', 'Afternoon. Dig in - and tell me if you want a steer.', 'Hi there. What are you in the mood for?'], seed));
    if (/\b(thanks|thank you|cheers|ta|bye|goodbye|see you|that s all|nothing else)\b/.test(query))
      return result('thanks', choose(['Anytime. Happy digging.', 'Cheers. Come find me if you want another steer.', 'Enjoy them. Mind the sleeves.'], seed));
    if (/\b(how (do|does|can|much)|price|cost|credits?|pay|buy|checkout|check out|library|generate|demo|taste|preview|how it works|what is this)\b/.test(query))
      return result('help', PRICE_LINE);
    if (/\b(what do you (have|stock|sell|carry)|sections?|genres?|categories|what s (here|in store|in stock)|browse|everything)\b/.test(query))
      return result('sections', sections());

    // The lead-in is how people ask, not what they ask for. "i love The Congos" must not go
    // looking for records with "love" in them - there is an artist called Dorothy Love Coates.
    const cleaned = query
      .replace(/\b(i|we) (really |absolutely |totally )?(love|like|dig|adore|enjoy|listen to|am into|m into)\b/g, ' ')
      .replace(/\b(something|anything|stuff|records?|music|albums?|more) (like|similar to|in the style of)\b/g, ' ')
      .replace(/\b(similar to|sounds? like|fans? of|reminds? me of|in the style of|like)\b/g, ' ')
      .replace(/\s+/g, ' ').trim() || query;
    const when = timeIn(query);
    const artist = findArtist(cleaned);
    const genresWanted = genresIn(cleaned);
    const vibes = vibesIn(cleaned);

    // "another", "more", "something else": the next few from the last answer
    if (/^(another|more|next|something else|else|again|others?|different|what else)\b/.test(query) && state.last) {
      const { ranked, label } = state.last;
      const rest = ranked.filter((i) => !state.seen.has(i.d.r.id));
      if (rest.length) return { intent: 'more', ...present(`More ${label}:`, diversify(rest), { quiet: true }), suggestions: SUGGESTIONS() };
      return result('more', 'That is everything I have on that, honestly. Try a different mood or a decade?');
    }

    // where is something
    if (/\bwhere\b/.test(query) || /\b(find|section|aisle|bin)\b.*\b(for|of)\b/.test(query)) {
      const ranked = rank(query, { artist, genresWanted, vibes: [], when: {} });
      const top = ranked.length ? diversify(ranked, 1) : [];
      if (top.length) {
        const { picks } = present('', top);
        const p = picks[0];
        const spot = p.place && p.place.text ? p.place.text : 'on the shop floor';
        return result('where', `${p.genre} — the ${p.bin} bin is ${spot}. I'll point you there.`, picks);
      }
      return result('where', `Not sure what you're after - try a section like jazz or reggae, or an artist. ${sections()}`);
    }

    // what's new: a rotating handful, the same for everyone in a given week
    if (/\b(new|latest|just in|arrivals?|fresh|recently|this week|came in|come in)\b/.test(query) && !artist && !genresWanted.length) {
      const arrivals = [...index.docs].sort((a, b) => hash(week() + a.r.id) - hash(week() + b.r.id)).map((d) => ({ d, score: 1, why: ['new in this week'] }));
      state.last = { ranked: arrivals, label: 'new arrivals' };
      return { intent: 'new', ...present('Just in this week:', diversify(arrivals), { quiet: true }), suggestions: SUGGESTIONS() };
    }

    // surprise me / recommend something, with no particular lead
    const substantive = meaningful(tokens(query)).filter((t) => !['surprise', 'recommend', 'suggest', 'pick', 'good', 'best', 'staff', 'choice', 'dealer', 'favourite', 'favorite', 'whatever', 'idk', 'dunno'].includes(t));
    if ((/\b(surprise|recommend|suggest|staff pick|dealers? choice|your pick|your favou?rite|whatever|dunno|idk|anything good)\b/.test(query)) && !substantive.length && !artist && !genresWanted.length && !vibes.length) {
      const pool = index.docs.filter((d) => !state.seen.has(d.r.id)).map((d) => ({ d, score: 1, why: ['staff pick'] }))
        .sort((a, b) => hash(query + state.turns + a.d.r.id) - hash(query + state.turns + b.d.r.id));
      state.last = { ranked: pool, label: 'picks' };
      return { intent: 'surprise', ...present(choose(['Right, trust me on these:', 'Dealer\'s choice, then:', 'Try these - nobody ever regrets a gamble:'], seed), diversify(pool), { quiet: true }), suggestions: SUGGESTIONS() };
    }

    // the real work: search
    const ranked = rank(cleaned, { artist, genresWanted, vibes, when, avoid: state.seen });
    if (!ranked.length || ranked[0].score < 2.5) {
      return result('nomatch', `Hm, nothing jumps out for "${raw.trim()}". Try an artist you like, a decade, or a mood - dark, mellow, weird, upbeat. Or say "what do you have".`);
    }
    const label = artist ? `for fans of ${artist.display}` : genresWanted.length ? genresWanted.map(genreLabel).join(' / ').toLowerCase() : vibes.length ? vibes.map((v) => v.name).join(' and ') : 'matches';
    state.last = { ranked, label };
    const picks = diversify(ranked);
    const exact = artist && ranked[0].why.some((w) => w.startsWith('for fans of') || w.startsWith('by '));
    const intro = exact
      ? choose([`Oh, good taste. If you like ${artist.display}, try:`, `${artist.display} fans tend to love these:`, `If it's ${artist.display} you're after, I'd start here:`], seed)
      : artist
        ? `I don't have ${artist.display} as such, but the closest I can do:`
        : choose(['Right, I know the sort of thing:', 'Okay - try these:', 'I think you\'ll like these:'], seed);
    return { intent: exact ? 'artist' : 'search', ...present(intro, picks, { quiet: !!exact }), suggestions: SUGGESTIONS() };
  }

  function greet() {
    return { intent: 'greeting', reply: 'Welcome to NSPIRE RECORDS. Tell me what you like - a mood, an artist, a decade - and I\'ll point you at something. Or just dig; it\'s all good.',
      picks: [], suggestions: SUGGESTIONS() };
  }

  // A pick for a record by id, or null: how an answer from outside (a language model) is held to
  // the catalogue. Whatever it names, only real records get through.
  const docById = new Map(index.docs.map((d) => [d.r.id, d]));
  function pickFor(id) {
    const d = docById.get(id);
    return d ? toPick({ d, why: [] }) : null;
  }

  return { ask, greet, pickFor, stats: () => ({ records: index.docs.length, artists: artistNames.size, turns: state.turns }), state };
}

export { createClerkBrain, normalise, tokens };
